import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { portalP12Api, type VisibilityEntity } from '../services/portalP12Api';
import { toast } from '../store/toastStore';

/**
 * P-12 (D-102) — "🔒 לא להציג ללקוח" for a project, a milestone or a task.
 * Self-contained: it reads and saves its own state through
 * /api/customer-visibility, so a form only mounts it (one line). Saves on
 * change. Hidden entirely for whoever may not change it (the server decides:
 * general manager, the customer's account manager, the project's manager and
 * — D-115 / QX-47 — the project's team lead). D-123: it hides the item ONLY
 * on the portal project page (its milestone / task row and bar) and in
 * "עובדים עכשיו על"; the hour report and the monthly billing report show it
 * as usual, its hours count in every total and come off the bank.
 */
export default function HideFromCustomerToggle({ entity, id, className = '' }: {
  entity: VisibilityEntity;
  id: string | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['customer-visibility', entity, id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => portalP12Api.getVisibility(entity, id!),
    enabled: !!id,
    retry: false,
  });
  const mut = useMutation({
    mutationFn: (hidden: boolean) => portalP12Api.setVisibility(entity, id!, hidden),
    onSuccess: (r) => {
      qc.setQueryData(key, { success: true, data: { hidden_from_customer: r.data.hidden_from_customer, can_edit: true } });
      toast('success', r.data.hidden_from_customer ? t('portalP12.hide.nowHidden') : t('portalP12.hide.nowShown'));
    },
    onError: () => toast('error', t('portalP12.hide.error')),
  });
  const state = q.data?.data;
  if (!id || !state || !state.can_edit) return null;
  const inputId = `hide-from-customer-${entity}-${id}`;
  return (
    <label htmlFor={inputId} title={t('portalP12.hide.hint')} className={`inline-flex items-center gap-2 text-sm text-surface-800 ${className}`}>
      <input
        id={inputId}
        type="checkbox"
        checked={state.hidden_from_customer}
        disabled={mut.isPending}
        onChange={(e) => mut.mutate(e.target.checked)}
      />
      <Lock size={14} className="text-surface-500" aria-hidden="true" />
      {t('portalP12.hide.label')}
    </label>
  );
}
